/**
 * AdventureList Component
 * 
 * Displays a list of available adventures as simple title images (like InventoryList).
 * Used in rich text rendering for <Adventure-List> tags.
 * Items are clickable and will start the adventure when clicked.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { AdventureHeader } from '../../types';
import { AdventureTitleImage } from './AdventureTitleImage';
import useGameStore from '../../stores/gameStore';
import { useAuth } from '../../hooks/useAuth';

export interface AdventureListProps {
  /**
   * Optional filter to exclude system adventures (e.g., 'storynity')
   */
  excludeSystemAdventures?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
  
  /**
   * Optional custom button color for styling adventure buttons
   */
  customColor?: string;
}

/**
 * AdventureList component for displaying lists of adventures in rich text
 */
export const AdventureList: React.FC<AdventureListProps> = ({
  excludeSystemAdventures = true,
  className = '',
  style,
  customColor
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { createSession, systemAdventureId } = useGameStore();
  const [adventures, setAdventures] = useState<Array<{ id: string; header: AdventureHeader }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingAdventureId, setStartingAdventureId] = useState<string | null>(null);

  const loadAdventures = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get list of adventure IDs
      const adventureIds = await apiService.listAdventures();
      
      // Fetch headers for all adventures
      const adventureHeaders = await Promise.all(
        adventureIds.map(async (id) => {
          try {
            const header = await apiService.getAdventureHeader(id);
            return { id, header };
          } catch (err) {
            console.warn(`Failed to load adventure header for ${id}:`, err);
            return null;
          }
        })
      );

      // Filter out null entries, entries with missing header, and system adventures if requested
      const validAdventures = adventureHeaders.filter((adventure) => {
        if (!adventure || !adventure.header) return false;
        if (excludeSystemAdventures && adventure.id === systemAdventureId) {
          return false;
        }
        return true;
      }) as Array<{ id: string; header: AdventureHeader }>;

      setAdventures(validAdventures);
    } catch (err) {
      console.error('Failed to load adventures:', err);
      setError(err instanceof Error ? err.message : 'Failed to load adventures');
    } finally {
      setIsLoading(false);
    }
  }, [excludeSystemAdventures, systemAdventureId]);

  useEffect(() => {
    loadAdventures();
  }, [loadAdventures]);

  const handleAdventureClick = async (adventureId: string) => {
    // Check authentication status
    if (!isAuthenticated) {
      setError('You must be logged in to start an adventure. Please log in first.');
      return;
    }

    setStartingAdventureId(adventureId);
    try {
      const { sessionId, playerId } = await createSession(adventureId);
      // Navigate directly to the scene with both sessionId and playerId
      if (playerId) {
        navigate(`/scene/${sessionId}/${playerId}`);
      } else {
        // Fallback to sessionId only if no player ID available
        navigate(`/scene/${sessionId}`);
      }
    } catch (error) {
      console.error('Failed to start adventure:', error);
      setError(error instanceof Error ? error.message : 'Failed to start adventure. Please try again.');
      setStartingAdventureId(null);
    }
  };

  if (isLoading) {
    return (
      <div 
        className={`adventure-list loading ${className}`} 
        style={style}
        data-testid="adventure-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">Loading adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`adventure-list error ${className}`} 
        style={style}
        data-testid="adventure-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400">Error loading adventures: {error}</p>
        </div>
      </div>
    );
  }

  if (adventures.length === 0) {
    return (
      <div 
        className={`adventure-list empty ${className}`} 
        style={style}
        data-testid="adventure-list-empty"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">No adventures available</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`adventure-list ${className}`} 
      style={style}
      data-testid="adventure-list"
    >
      <div
        className="adventure-list-items flex flex-wrap justify-center"
        style={{ gap: '1rem', rowGap: '0.5rem' }}
        data-testid="adventure-items-list"
      >
        {adventures.map(({ id, header }) => {
          const isStarting = startingAdventureId === id;
          
          // Apply custom color styling if provided
          const getColorWithAlpha = (color: string, alpha: string) => {
            if (color.startsWith('#')) {
              return `${color}${alpha}`;
            }
            return color;
          };
          
          const buttonStyle: React.CSSProperties = { 
            width: '512px', 
            maxWidth: '100%', 
            marginBottom: '0.5rem' 
          };
          
          if (customColor && !isStarting && !startingAdventureId) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }
          
          return (
            <button
              key={id}
              onClick={() => handleAdventureClick(id)}
              disabled={isStarting || !!startingAdventureId}
              className="adventure-item-button inventory-item-button"
              style={buttonStyle}
              data-testid={`adventure-item-${id}`}
              data-adventure-id={id}
              aria-label={`Start ${header.title}`}
            >
              <div className="adventure-item-image" style={{ width: '100%' }}>
                {isStarting ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-gray-400 italic">Starting...</span>
                  </div>
                ) : (
                  <AdventureTitleImage
                    titleImageId={header.titleImageId}
                    variant="card"
                    alt={header.title}
                    responsive={true}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdventureList;

